<template>
  <v-tooltip v-if="alerts.length > 0" location="bottom">
    <template v-slot:activator="{ props }">
      <v-btn
        v-bind="props"
        data-cy="deviceAlerts"
        class="popover-status-icon device-errors-icon"
        variant="text"
      >
        <v-icon
          data-cy="warningTriangleIcon"
          :color="hasStelAlarm ? 'red' : 'grey'"
          icon="mdi-alert"
        />
      </v-btn>
    </template>
    <span data-cy="deviceAlertsTooltip">
      <div v-for="alert in alerts" :key="alert">- {{ alert }}</div>
    </span>
  </v-tooltip>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useLocale } from '@/composables/useLocale'
import type { GetterByDeviceId } from '@/components/devicePopover/types'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS } from '@/store/devices'

export default defineComponent({
  name: 'DeviceAlerts',
  props: {
    deviceId: {
      type: String,
      default: ''
    }
  },
  setup() {
    const { getLocaleMessage } = useLocale()
    return { getLocaleMessage }
  },
  computed: {
    deviceStatus(): string[] {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_DEVICE_STATUS_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string[] | undefined>

      return getter(this.deviceId) || []
    },
    sensorStatus(): string[] {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SENSOR_STATUS_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string[] | undefined>

      return getter(this.deviceId) || []
    },
    errorState(): string[] {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_ERROR_STATE_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string[] | undefined>

      return getter(this.deviceId) || []
    },
    deviceAlarms(): string[] {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_DEVICE_ALARMS_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string[] | undefined>

      return getter(this.deviceId) || []
    },
    deviceAlarmsMessages(): string[] {
      const messages = this.constructLocaleMessages('device.alarms', this.deviceAlarms)
      return this.modifyStelAlarmMessage(messages)
    },
    hasStelAlarm(): boolean {
      return this.deviceAlarms.includes('STEL_ALARM')
    },
    measureAlarms1(): string[] {
      return this.deviceAlarms.filter((alarm) => alarm.startsWith('ALARM1'))
    },
    errorStateMessages(): string[] {
      return this.constructLocaleMessages('device.errors.device.errorState', this.errorState)
    },
    alerts(): string[] {
      return [
        ...this.deviceStatus,
        ...this.sensorStatus,
        ...this.errorStateMessages,
        ...this.deviceAlarmsMessages
      ]
    }
  },
  methods: {
    constructLocaleMessages(localePath: string, entries: string[]): string[] {
      const localeFields = this.getLocaleMessage(localePath) as Record<string, unknown>
      const messages: string[] = []
      if (localeFields && entries.length) {
        Object.keys(localeFields).forEach((key) => {
          const index = entries.indexOf(key.toLocaleUpperCase())
          if (index !== -1) {
            const result = this.$t(`${localePath}.${key}`)
            if (typeof result === 'string') {
              messages.push(result)
            }
          }
        })
      }
      return messages
    },
    modifyStelAlarmMessage(messages: string[]): string[] {
      // if there are zero or more than one Alarm1 message, return the messages array as is
      // no need to modify the STEL message
      if (this.measureAlarms1.length !== 1) {
        return messages
      }

      // split the Alarm1 message to extract the channel e.g ALARM1_PM1 in 'ALARM1' and 'PM1'
      const parts = this.measureAlarms1[0].split('_')
      // if the parts array has less than 2 elements, return the messages array as is
      // this means the Alarm1 message is not in the expected format
      if (parts.length < 2) {
        return messages
      }

      // parts[0] should be 'ALARM1'
      // parts[1] should be 'PM1', 'PM2', 'PM4', 'PM10' 'PM2_5' or 'TOTAL'
      // parts[2] if exists should be the decimal of the channel number e.g. in PM2_5, it would be '5'
      let channel = ''
      if (parts[1] === 'TOTAL') {
        // Handle special case for 'ALARM1_TOTAL'
        channel = 'Total'
      } else {
        // Handle regular PM channels
        // Extracts the PM channel number and appends the decimal if it exists
        channel = parts[1].slice(2)
        if (parts[2]) {
          channel += `.${parts[2]}`
        }

        // Ensure the channel is in the correct format, e.g. PM 1.0 or PM 4.0
        if (channel.length < 2) {
          channel += '.0'
        }
      }

      // Creates the new STEL message and updates the existing STEL message in the messages array
      const newMessage = this.$t('device.alarms.stel_alarm_with_pm_channel', { channel })
      const updatedMessages = [...messages]
      const stelIndex = updatedMessages.findIndex(
        (message): message is string => typeof message === 'string' && message.includes('STEL')
      )

      if (stelIndex !== -1 && typeof newMessage === 'string') {
        updatedMessages[stelIndex] = newMessage
      }

      return updatedMessages
    }
  }
})
</script>
